import _ from 'lodash';
import HTTPStatus from 'http-status';

import { API_ROOT, IS_PROD, IS_STAGING } from '../constants';
import { HTTP_METHOD_GET, HTTP_METHOD_POST } from '../constants/httpMethod';

import { logoutUser } from '../services/authentication/logout/actions';

import { toi18nKey } from '../utils';

import { Response } from '../objects/Response';

const NO_TOKEN_MESSAGE = "No token saved, you can't fetch authenticated ressource without token.";
const TYPES_SIZE_MESSAGE = 'Expected an array of two or three string types.';
const HEADER_CONTENT_TYPE_JSON = { 'Content-Type': 'application/json' };

const formatResponse = ({ status }) => ({ payload, message }) =>
    new Response(status, payload, message);

const formatResponseData = (status, data) => {
    return new Response(status, {}, 'Binary data', data);
};

const formatRawResponse = (responseNetwork, blob) => {
    if (blob) {
        return responseNetwork
            .blob()
            .then(blob => formatResponseData(responseNetwork.status, blob))
            .catch(formatResponse(responseNetwork));
    } else {
        return responseNetwork
            .json()
            .then(formatResponse(responseNetwork))
            .catch(formatResponse(responseNetwork));
    }
};
/**
 *
 * @param {String} endpoint url vers le l'api serveur, bien mettre un slash en préfixe
 * @param {*} authenticated si l'appel doit etre en mode authentifié
 * @param {*} token le token pour identifier le membre
 */
function callApi(endpoint, headers, method, body, authenticated, token, blob) {
    // if body is FormData(use with form including files) we don't put headers and don't stringify it
    const isFormDataBody = body instanceof FormData;

    let config = {
        credentials: 'same-origin',
        method,
        headers: isFormDataBody ? {} : HEADER_CONTENT_TYPE_JSON
    };

    if (headers) {
        config = {
            ...config,
            headers: headers
        };
    }

    if (authenticated && endpoint !== '/auth/login') {
        if (token) {
            config = {
                ...config,
                headers: { ...config.headers, Authorization: `Bearer ${token}` }
            };
        }
    }

    if (body) {
        if (!isFormDataBody) {
            body = JSON.stringify(body);
        }
        config = { ...config, body };
    }

    return fetch(`${API_ROOT}${endpoint}`, config)
        .then(responseNetwork => {
            return formatRawResponse(responseNetwork, blob);
        })
        .then(response => {
            if (response.status >= HTTPStatus.BAD_REQUEST) {
                return Promise.reject(response);
            } else {
                return response;
            }
        })
        .catch(response => {
            return Promise.reject(
                _.isObject(response) && response.status
                    ? response
                    : new Response(HTTPStatus.SERVICE_UNAVAILABLE)
            );
        });
}

export const CALL_API = Symbol('Call API');

/**
 * Effectue un appel vers l'api serveur en mode authentifié ou non
 * Dans votre reducer, pour récupérer le retour la variable se nomme newState
 * exemple d'utilisation :
 * [CALL_API]: {
 *      endpoint: '/url',
 *      types:[ACTION_TYPE_REQUEST, ACTION_TYPE_SUCCESS, ACTION_TYPE_ERROR],
 *      authenticated: true si on veut faire appel à une api en mode authentifié sinon on le met pas,
 *      shouldCallAPI: function qui retourne true si il faut faire le fetch sinon false
 *      payload: données que l'on veut passer vers le reducer
 * }
 * /!\ Attention à mettre dans le bon ordre vos actionTypes: le type request puis le type success puis le type error
 *
 *
 * Format de retour :
 * Si success :
 * {
 *  type: XX_SUCCESS,
 *  response: Objet Response contenant les données retourné par le serveur (payload),
 *  X attributs mis dans le parametre payload de callAPI
 * }
 * Si erreur :
 * {
 *  type: XXX_ERROR,
 *  response: Objet Response contenant le message d'erreur retourné par le serveur (message),
 * }
 */

export default ({ dispatch, getState }) => next => action => {
    const callAPI = action[CALL_API];

    // le middleware ne sera pas appliqué à toutes les actions, seulement à celle utilisant la forme de ce middleware
    if (typeof callAPI === 'undefined') {
        return next(action);
    }
    const token =
        getState().authentication && getState().authentication.token
            ? getState().authentication.token
            : '';

    const {
        endpoint,
        headers = null,
        types,
        authenticated = !_.isEmpty(token),
        shouldCallAPI = () => true,
        payload = {},
        body = null,
        blob = false,
        method = body ? HTTP_METHOD_POST : HTTP_METHOD_GET,
        successCallback = null,
        errorCallback = null
    } = callAPI;

    let overridedHeaders = null;
    if ((IS_STAGING || IS_PROD) && _.isEmpty(token)) {
        overridedHeaders = { ...headers };
        if (endpoint !== '/service-client') {
            overridedHeaders = {
                ...overridedHeaders,
                'Content-Type': 'application/json;charset=UTF-8'
            };
        }
    }

    // le middleware ne sera pas appliqué à toutes les actions
    if (!types || !endpoint) {
        return next(action);
    }

    if (
        !Array.isArray(types) ||
        types.length !== 3 ||
        !types.every(type => typeof type === 'string')
    ) {
        throw new Error(TYPES_SIZE_MESSAGE);
    }

    if (!shouldCallAPI(getState())) {
        return;
    }

    const [requestType, successType, errorType] = types;

    if (authenticated && !token) {
        window.location.href = '/';
        throw new Error(NO_TOKEN_MESSAGE);
    }

    dispatch({ type: requestType, ...payload });

    // on passe authenticated dans nos données pour distinguer les appels à authentification et non authentifié
    return callApi(endpoint, overridedHeaders, method, body, authenticated, token, blob).then(
        response => {
            response.message = findResponseMessage(
                getState().i18n.HTTP_STATUS_CODE_MESSAGE,
                response.status
            );
            next({ type: successType, response, ...payload, authenticated });
            successCallback && successCallback(response, dispatch);
        },
        response => {
            if (response.status === HTTPStatus.UNAUTHORIZED) {
                dispatch(logoutUser());
            }

            response.message = findResponseMessage(
                getState().i18n.HTTP_STATUS_CODE_MESSAGE,
                response
            );
            next({ type: errorType, response });
            errorCallback && errorCallback(response);
        }
    );
};

/**
 * Return the message associated by the api response's http status code or the message
 * @param {*} mapKeyContents : map containing key/message for api return
 * @param {*} response : Object Response containing message and status code
 */
const findResponseMessage = (mapKeyContents, response) => {
    if (!mapKeyContents) {
        throw new Error('State must not be empty or undefined!');
    }
    const key = response.message ? response.message : HTTPStatus[response.status];
    const keyFormatted = toi18nKey(key);
    return mapKeyContents[keyFormatted] ? mapKeyContents[keyFormatted] : mapKeyContents.DEFAULT;
};



// WEBPACK FOOTER //
// ./src/middlewares/api.js